/* Couple set cart guard (WAI-673).
 *
 * Every couple set in the cart is a pair of lines labelled with the hidden property
 * `_couple_set` = "<set id>|<variant>|<variant>" (hidden from shoppers; shown on the admin order).
 * One discount code covers all sets in the cart.
 *
 * Before any cart change is sent (quantity, remove, update), the guard works out the cart as it
 * will be. If a set would lose a piece, it first asks the couple-set service for a code covering
 * only the sets still complete, puts that code on the cart, and only then lets the change through.
 * Intact sets therefore keep their discount throughout; a broken set loses its share.
 * After changes it strips the label from leftover pieces of broken sets. It never runs on a timer.
 */
(function () {
  const config = window.CoupleSetConfig || {};
  if (!config.workerUrl || window.CoupleSetGuard) return;

  const LABEL = 'Couple set';
  const KEY = '_couple_set';
  const INTERNAL = 'X-Couple-Set';
  const CODE_RE = new RegExp(`^${config.codePrefix || 'CS'}-[A-Z0-9]{8}$`);
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);

  function internal(path, body) {
    return nativeFetch(`${root}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [INTERNAL]: '1' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((r) => {
      if (!r.ok) throw new Error(`${path} ${r.status}`);
      return r.json();
    });
  }

  function parseLabel(item) {
    const raw = item.properties && item.properties[KEY];
    if (!raw) return null;
    const [id, ...variants] = String(raw).split('|');
    return id && variants.length === 2 ? { id, variants } : null;
  }

  // Sets in a list of {variant_id, quantity, properties} items, with whether each is complete.
  function setsIn(items) {
    const sets = {};
    for (const item of items) {
      const label = parseLabel(item);
      if (!label) continue;
      const s = (sets[label.id] = sets[label.id] || { id: label.id, variants: label.variants, have: {} });
      s.have[item.variant_id] = (s.have[item.variant_id] || 0) + item.quantity;
    }
    for (const s of Object.values(sets)) {
      const need = {};
      for (const v of s.variants) need[v] = (need[v] || 0) + 1;
      s.intact = Object.entries(need).every(([v, n]) => (s.have[v] || 0) >= n);
    }
    return sets;
  }

  function couponOn(cart) {
    return (cart.discount_codes || []).map((d) => d.code).find((c) => CODE_RE.test(c)) || null;
  }

  function linesFor(items) {
    return items
      .map((i) => ({ label: parseLabel(i), variant: String(i.variant_id), quantity: i.quantity }))
      .filter((l) => l.label && l.quantity > 0)
      .map((l) => ({ variant: l.variant, quantity: l.quantity, set: l.label.id }));
  }

  // The cart's items as they will be after a /cart/change or /cart/update request.
  function project(items, path, body) {
    const next = items.map((i) => ({ ...i }));
    const setQty = (match, qty) => {
      const q = Math.max(0, Number(qty) || 0);
      if (typeof match === 'number') {
        if (next[match]) next[match].quantity = q;
        return;
      }
      const key = String(match);
      const byKey = next.find((i) => i.key === key);
      if (byKey) byKey.quantity = q;
      else for (const i of next) if (String(i.variant_id) === key) i.quantity = q; // variant id: applies to its first line in Shopify; treat all as changed (conservative)
    };
    if (/change/.test(path)) {
      if (body.line !== undefined) setQty(Number(body.line) - 1, body.quantity);
      else if (body.id !== undefined) setQty(body.id, body.quantity);
    } else if (body.updates) {
      if (Array.isArray(body.updates)) body.updates.forEach((q, idx) => setQty(idx, q));
      else for (const [k, q] of Object.entries(body.updates)) setQty(k, q);
    }
    return next.filter((i) => i.quantity > 0);
  }

  function parseBody(body) {
    if (!body) return null;
    try {
      if (typeof body === 'string') {
        if (body.trim().startsWith('{')) return JSON.parse(body);
        body = new URLSearchParams(body);
      }
      if (body instanceof FormData || body instanceof URLSearchParams) {
        const out = {};
        for (const [k, v] of body.entries()) {
          const m = k.match(/^updates\[(.*)\]$/);
          if (m) (out.updates = out.updates || {})[m[1]] = v;
          else out[k] = v;
        }
        return out;
      }
    } catch (e) {
      return null;
    }
    return null;
  }

  async function askService(payload) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 8000);
    try {
      const res = await nativeFetch(config.workerUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: ctrl.signal,
      });
      return res.ok ? res.json() : null;
    } finally {
      clearTimeout(t);
    }
  }

  // Runs before a cart change. Swaps the code first when the change would break a set.
  async function beforeChange(path, body) {
    const cart = await internal('cart.js');
    const code = couponOn(cart);
    const before = setsIn(cart.items);
    if (!code || !Object.keys(before).length) return;
    const after = setsIn(project(cart.items, path, body));
    const breaks = Object.values(before).some((s) => s.intact && !(after[s.id] && after[s.id].intact));
    if (!breaks) return;
    const result = await askService({ cart: { code, lines: linesFor(project(cart.items, path, body)) } });
    if (!result || result.unchanged) return;
    // The new code needs only the sets still intact, which the cart already holds, so it applies now.
    await internal('cart/update.js', { discount: result.code || '' });
  }

  // After a cart change: finish a set switch the builder page didn't get to, and take the label off leftover
  // pieces of broken sets. A code no set uses stays on the cart, as with any Shopify code: Shopify marks it not
  // applicable and it discounts nothing unless that set's exact pieces come back (Howard, 2026-10-23).
  async function tidy() {
    let cart = await internal('cart.js');
    // A set added on the builder page whose code switch didn't finish (e.g. the tab closed in between).
    const pending = readPending();
    if (pending) {
      const added = cart.items.some((i) => (parseLabel(i) || {}).id === pending.setId);
      if (added && couponOn(cart) !== pending.code) {
        await internal('cart/update.js', { discount: pending.code });
        cart = await internal('cart.js');
      }
      if (added || Date.now() - pending.at > 10 * 60 * 1000) clearPending();
    }
    const sets = setsIn(cart.items);
    let changed = false;
    for (let guard = 0; guard < 10; guard++) {
      const current = guard === 0 ? cart : await internal('cart.js');
      const currentSets = guard === 0 ? sets : setsIn(current.items);
      const orphan = current.items.find((i) => {
        const l = parseLabel(i);
        return l && currentSets[l.id] && !currentSets[l.id].intact;
      });
      if (!orphan) break;
      // Shopify can blank a line property but not delete it, so swap the line for an unlabelled one.
      const properties = { ...orphan.properties };
      delete properties[KEY];
      delete properties[LABEL];
      await internal('cart/change.js', { id: orphan.key, quantity: 0 });
      await internal('cart/add.js', { items: [{ id: orphan.variant_id, quantity: orphan.quantity, properties }] });
      changed = true;
    }
    if (changed) document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  const PENDING = 'couple-set-pending';
  function readPending() {
    try {
      return JSON.parse(localStorage.getItem(PENDING) || 'null');
    } catch (e) {
      return null;
    }
  }
  function clearPending() {
    try {
      localStorage.removeItem(PENDING);
    } catch (e) {
      /* storage unavailable */
    }
  }
  function setPending(code, setId) {
    try {
      localStorage.setItem(PENDING, JSON.stringify({ code, setId, at: Date.now() }));
    } catch (e) {
      /* storage unavailable */
    }
  }

  // One tidy at a time, in the order of the changes that asked for it. No timers: a timed tidy once dropped a
  // new set's code before its pieces had landed.
  let tidyQueue = Promise.resolve();
  function queueTidy() {
    tidyQueue = tidyQueue.then(() => tidy()).catch(() => {});
  }

  // The couple-set code that is doing something: on the cart with at least one complete set. The builder
  // merges new sets into this code; a leftover code with no set is replaced instead.
  function activeCoupon(cart) {
    return Object.values(setsIn(cart.items)).some((s) => s.intact) ? couponOn(cart) : null;
  }

  function hasInternalHeader(init) {
    const h = init && init.headers;
    if (!h) return false;
    if (h instanceof Headers) return h.has(INTERNAL);
    return Object.keys(h).some((k) => k.toLowerCase() === INTERNAL.toLowerCase());
  }

  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    let path = '';
    try {
      path = new URL(url, location.origin).pathname;
    } catch (e) {
      /* not a URL we care about */
    }
    const cartWrite = method === 'POST' && /\/cart\/(change|update|clear)(\.js)?$/.test(path) && !hasInternalHeader(init);
    if (cartWrite && !/clear/.test(path)) {
      const body = parseBody(init && init.body);
      if (body && (/change/.test(path) || body.updates)) {
        try {
          await beforeChange(path, body);
        } catch (e) {
          // Never block the shopper's change; the tidy step and the next change retry.
        }
      }
    }
    const response = await nativeFetch(input, init);
    if (cartWrite) queueTidy();
    return response;
  };

  window.CoupleSetGuard = { setsIn, couponOn: activeCoupon, linesFor, parseLabel, CODE_RE, LABEL, KEY, internal, tidy, setPending, clearPending };
  queueTidy();
})();
